import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Package, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const { clear } = useCart();
  const tries = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) { clear(); clearedRef.current = true; }
        } else if (data.payment_status === "failed" || data.status === "failed" || data.status === "expired") {
          setStatus("failed");
        } else if (tries.current++ < 15) {
          setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch { if (!cancelled) setStatus("error"); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <div className="container-tight py-24 max-w-xl mx-auto text-center" data-testid="payment-success-page">
      {status === "polling" && (
        <>
          <Loader2 className="w-16 h-16 mx-auto text-[#FF5C00] animate-spin mb-6" />
          <h1 className="font-display font-black text-3xl mb-2">CONFIRMING PAYMENT...</h1>
          <p className="text-zinc-500">Please wait — this usually takes a few seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 mx-auto text-green-400 mb-6" />
          <div className="font-mono text-xs tracking-widest text-[#FF5C00] mb-2">// ORDER CONFIRMED</div>
          <h1 className="font-display font-black text-4xl mb-3">PAYMENT SUCCESSFUL</h1>
          <p className="text-zinc-400 mb-8">Your order has been received. Interstate PC will follow up with confirmation and next steps by email.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/account/orders"><Button data-testid="view-orders-btn" className="bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider"><Package className="w-4 h-4 mr-2" />VIEW ORDERS</Button></Link>
            <Link to="/catalog"><Button variant="outline" className="border-zinc-700 bg-transparent rounded-sm font-mono tracking-wider">CONTINUE SHOPPING <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "error" || status === "timeout") && (
        <>
          <XCircle className="w-16 h-16 mx-auto text-red-400 mb-6" />
          <h1 className="font-display font-black text-3xl mb-2">
            {status === "timeout" ? "STILL PROCESSING" : "PAYMENT NOT COMPLETED"}
          </h1>
          <p className="text-zinc-400 mb-6">
            {status === "timeout" ? "This is taking longer than expected. Check your orders in a few minutes." : "Something went wrong. Please try again."}
          </p>
          <Link to="/cart"><Button className="bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider">BACK TO CART</Button></Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="container-tight py-24 max-w-xl mx-auto text-center" data-testid="payment-cancel-page">
      <XCircle className="w-16 h-16 mx-auto text-zinc-500 mb-6" />
      <h1 className="font-display font-black text-3xl mb-2">CHECKOUT CANCELLED</h1>
      <p className="text-zinc-500 mb-6">No charges were made. Your cart is still saved.</p>
      <div className="flex gap-3 justify-center">
        <Button data-testid="return-cart-btn" onClick={() => nav("/cart")} className="bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider">RETURN TO CART</Button>
        <Button onClick={() => nav("/catalog")} variant="outline" className="border-zinc-700 bg-transparent rounded-sm font-mono tracking-wider">KEEP SHOPPING</Button>
      </div>
    </div>
  );
}
